import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Card, Table, Button, Switch, Space, Modal, Form,
  Input, InputNumber, Row, Col, Typography, notification,
} from 'antd';
import { PlusOutlined, EditOutlined, StarOutlined } from '@ant-design/icons';
import { baseUrl } from '../../config';
import { clearPaymentDropdownCache } from '../../hooks/usePaymentDropdowns';

const { Title, Text } = Typography;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

export default function SpecialOccasionPage() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [saving, setSaving]     = useState(false);
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${baseUrl}/special-occasion/list`, { headers: authHeaders() });
      if (res.data.status) setItems(res.data.result || []);
    } catch (err) {
      notification.error({ message: 'Failed to load: ' + err.message });
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setEditing(null); form.resetFields(); setModalOpen(true); };
  const openEdit = (rec) => { setEditing(rec); form.setFieldsValue(rec); setModalOpen(true); };

  const handleSave = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${baseUrl}/special-occasion/${editing.id}`, values, { headers: authHeaders() });
        notification.success({ message: 'Category updated.' });
      } else {
        await axios.post(`${baseUrl}/special-occasion`, values, { headers: authHeaders() });
        notification.success({ message: 'Category created.' });
      }
      setModalOpen(false);
      clearPaymentDropdownCache();
      load();
    } catch (err) {
      notification.error({ message: err.response?.data?.message || err.message });
    } finally { setSaving(false); }
  };

  const handleToggle = async (rec) => {
    try {
      const res = await axios.patch(`${baseUrl}/special-occasion/${rec.id}/toggle`, {}, { headers: authHeaders() });
      clearPaymentDropdownCache();
      setItems(prev => prev.map(i => i.id === rec.id ? { ...i, is_active: res.data.is_active } : i));
    } catch (err) {
      notification.error({ message: 'Toggle failed: ' + err.message });
    }
  };

  const columns = [
    { title: '#', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: 'Category Name', dataIndex: 'category_name', key: 'category_name',
      render: v => <Text strong>{v}</Text>,
    },
    { title: 'Sort', dataIndex: 'sort_order', key: 'sort_order', width: 70 },
    {
      title: 'Status', key: 'status', width: 110,
      render: (_, rec) => (
        <Switch
          checked={rec.is_active === 1}
          onChange={() => handleToggle(rec)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
    {
      title: 'Action', key: 'action', width: 80,
      render: (_, rec) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(rec)}>Edit</Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '16px 24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>
            <StarOutlined style={{ marginRight: 8 }} />Special Occasion Categories
          </Title>
          <Text type="secondary">Manage special/suspense entry categories shown in Add Payment</Text>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd} style={{ background: '#7a0000' }}>
            Add Category
          </Button>
        </Col>
      </Row>

      <Card bodyStyle={{ padding: 0 }}>
        <Table
          dataSource={items}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
        />
      </Card>

      <Modal
        title={editing ? 'Edit Category' : 'Add Category'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        okText={editing ? 'Update' : 'Create'}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Category Name" name="category_name" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. Birthday, Marriage, Suspense-Entries" />
          </Form.Item>
          <Form.Item label="Sort Order" name="sort_order">
            <InputNumber min={0} style={{ width: '100%' }} placeholder="0" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
